import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { guideApi } from "@/lib/guide";
import { Plus, Edit, Trash2, Eye, EyeOff, BookOpen, Save, X, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  title: "", title_en: "", category: "", slug: "", summary: "", content: "",
  faq: [], official_resources: [], keywords: [], related_services: [], related_slugs: [],
  published: true, featured: false,
};

export default function AdminGuide() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, 'new' or slug
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [list, cats] = await Promise.all([
        axios.get(`${API}/admin/guide/articles`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
        guideApi.categories(),
      ]);
      setArticles(list); setCategories(cats);
    } catch { toast.error(t("فشل التحميل", "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = async (slug) => {
    try {
      const a = await guideApi.get(slug);
      setForm({ ...empty, ...a });
      setEditing(slug);
    } catch { toast.error(t("فشل التحميل", "Load failed")); }
  };
  const togglePublish = async (slug, currentlyPublished) => {
    try {
      await axios.post(`${API}/admin/guide/articles/${slug}/publish?published=${!currentlyPublished}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم", "Done"));
      load();
    } catch { toast.error(t("فشل", "Failed")); }
  };
  const remove = async (slug) => {
    if (!window.confirm(t("تأكيد الحذف؟", "Confirm delete?"))) return;
    try {
      await axios.delete(`${API}/admin/guide/articles/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      load();
    } catch { toast.error(t("فشل", "Failed")); }
  };
  const save = async () => {
    if (!form.title || !form.category || !form.slug || !form.summary || !form.content) {
      toast.error(t("املأ كل الحقول المطلوبة", "Fill required fields"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        keywords: typeof form.keywords === "string" ? form.keywords.split(",").map((s) => s.trim()).filter(Boolean) : form.keywords,
        related_services: typeof form.related_services === "string" ? form.related_services.split(",").map((s) => s.trim()).filter(Boolean) : form.related_services,
        related_slugs: typeof form.related_slugs === "string" ? form.related_slugs.split(",").map((s) => s.trim()).filter(Boolean) : form.related_slugs,
        faq: typeof form.faq === "string" ? JSON.parse(form.faq || "[]") : form.faq,
        official_resources: typeof form.official_resources === "string" ? JSON.parse(form.official_resources || "[]") : form.official_resources,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/guide/articles`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم النشر", "Created"));
      } else {
        await axios.put(`${API}/admin/guide/articles/${editing}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(t("تم الحفظ", "Saved"));
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("فشل الحفظ", "Save failed"));
    } finally { setSaving(false); }
  };

  const filtered = articles.filter((a) =>
    (!catFilter || a.category === catFilter) &&
    (!q || a.title.toLowerCase().includes(q.toLowerCase()) || a.slug.includes(q.toLowerCase()))
  );

  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none w-full";

  // ------- LIST -------
  if (editing === null) {
    return (
      <div className="glass-card rounded-xl overflow-hidden" data-testid="admin-guide-tab">
        <div className="p-4 border-b border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="font-bold text-white flex items-center gap-2">
            <BookOpen className="text-yellow-400" size={20} />
            {t("مقالات الدليل", "Guide Articles")}
            <Badge className="bg-slate-800 text-slate-300">{articles.length}</Badge>
          </h3>
          <div className="flex gap-2 flex-wrap">
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-white text-sm" data-testid="admin-guide-cat">
              <option value="">{t("كل الفئات", "All categories")}</option>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.ar}</option>)}
            </select>
            <div className="relative">
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("بحث...", "Search...")} className="bg-slate-900 border border-slate-700 rounded-lg pr-8 pl-2 py-2 text-white text-sm w-40" />
            </div>
            <Button onClick={startNew} className="btn-primary" data-testid="admin-new-article">
              <Plus size={16} className="ml-1" />{t("مقال جديد", "New Article")}
            </Button>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <ScrollArea className="h-[34rem]">
            <div className="divide-y divide-slate-800">
              {filtered.map((a) => (
                <div key={a.slug} className="p-4 hover:bg-slate-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`adm-art-${a.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-white truncate">{a.title}</p>
                      <Badge className="bg-slate-800 text-slate-300 text-[10px]">{a.category}</Badge>
                      {a.featured && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">{t("مميز", "Featured")}</Badge>}
                      {!a.published && <Badge className="bg-red-500/20 text-red-400 text-[10px]">{t("غير منشور", "Draft")}</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{a.slug} · {a.views || 0} {t("مشاهدة", "views")}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => startEdit(a.slug)} variant="outline" className="border-slate-700 text-white" data-testid={`edit-${a.slug}`}>
                      <Edit size={14} />
                    </Button>
                    <Button size="sm" onClick={() => togglePublish(a.slug, a.published)} className={a.published ? "bg-amber-500/20 text-amber-400" : "bg-green-500/20 text-green-400"} data-testid={`pub-${a.slug}`}>
                      {a.published ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                    <Button size="sm" onClick={() => remove(a.slug)} className="bg-red-500/20 text-red-400" data-testid={`del-${a.slug}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    );
  }

  // ------- FORM -------
  return (
    <div className="glass-card rounded-xl p-6 space-y-4" data-testid="admin-guide-form">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-white text-lg">{editing === "new" ? t("مقال جديد", "New Article") : t("تعديل مقال", "Edit Article")}</h3>
        <Button onClick={() => setEditing(null)} variant="outline" className="border-slate-700 text-white"><X size={16} className="ml-1" />{t("إلغاء", "Cancel")}</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm text-slate-400 block mb-1">{t("العنوان عربي *", "Title (AR) *")}</label>
          <Input className={inputCls} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="f-title" /></div>
        <div><label className="text-sm text-slate-400 block mb-1">{t("العنوان إنجليزي", "Title (EN)")}</label>
          <Input className={inputCls} value={form.title_en} onChange={(e) => setForm({...form, title_en: e.target.value})} /></div>
        <div><label className="text-sm text-slate-400 block mb-1">{t("الفئة *", "Category *")}</label>
          <select className={inputCls} value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="f-category">
            <option value="">--</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.ar}</option>)}
          </select></div>
        <div><label className="text-sm text-slate-400 block mb-1">Slug *</label>
          <Input className={inputCls} value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="my-article-slug" disabled={editing !== "new"} data-testid="f-slug" /></div>
      </div>
      <div><label className="text-sm text-slate-400 block mb-1">{t("الملخص *", "Summary *")}</label>
        <Textarea rows={2} className={inputCls} value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})} data-testid="f-summary" /></div>
      <div><label className="text-sm text-slate-400 block mb-1">{t("المحتوى الكامل (Markdown) *", "Full Content (Markdown) *")}</label>
        <Textarea rows={14} className={inputCls + " font-mono text-sm"} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} data-testid="f-content" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm text-slate-400 block mb-1">{t("كلمات مفتاحية (مفصولة بفواصل)", "Keywords (comma sep.)")}</label>
          <Input className={inputCls} value={Array.isArray(form.keywords) ? form.keywords.join(", ") : form.keywords} onChange={(e) => setForm({...form, keywords: e.target.value})} /></div>
        <div><label className="text-sm text-slate-400 block mb-1">{t("خدمات ذات صلة (مسارات /services?...)", "Related services (paths)")}</label>
          <Input className={inputCls} value={Array.isArray(form.related_services) ? form.related_services.join(", ") : form.related_services} onChange={(e) => setForm({...form, related_services: e.target.value})} /></div>
        <div><label className="text-sm text-slate-400 block mb-1">{t("Slugs مقالات مرتبطة", "Related article slugs")}</label>
          <Input className={inputCls} value={Array.isArray(form.related_slugs) ? form.related_slugs.join(", ") : form.related_slugs} onChange={(e) => setForm({...form, related_slugs: e.target.value})} /></div>
        <div><label className="text-sm text-slate-400 block mb-1">{t("FAQ (JSON: [{q,a}])", "FAQ (JSON: [{q,a}])")}</label>
          <Textarea rows={3} className={inputCls + " font-mono text-xs"} value={typeof form.faq === "string" ? form.faq : JSON.stringify(form.faq, null, 2)} onChange={(e) => setForm({...form, faq: e.target.value})} /></div>
        <div className="sm:col-span-2"><label className="text-sm text-slate-400 block mb-1">{t("مصادر رسمية (JSON: [{label,url}])", "Official Resources (JSON: [{label,url}])")}</label>
          <Textarea rows={3} className={inputCls + " font-mono text-xs"} value={typeof form.official_resources === "string" ? form.official_resources : JSON.stringify(form.official_resources, null, 2)} onChange={(e) => setForm({...form, official_resources: e.target.value})} /></div>
      </div>
      <div className="flex gap-4 items-center">
        <label className="flex items-center gap-2 text-slate-300 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} className="accent-yellow-500" />
          {t("منشور", "Published")}
        </label>
        <label className="flex items-center gap-2 text-slate-300 text-sm">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} className="accent-yellow-500" />
          {t("مميز", "Featured")}
        </label>
      </div>
      <Button onClick={save} disabled={saving} className="btn-primary w-full h-12" data-testid="save-article">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} className="ml-2" />{t("حفظ", "Save")}</>}
      </Button>
    </div>
  );
}
